<template>
  <div ref="wrapperRef" class="flex flex-col gap-y-2 relative">
    <div class="relative">
      <base-input
        v-model="inputValue"
        :additional-classes="['!pr-12']"
        :label="label"
        :placeholder="placeholder"
        :danger="danger"
        :maxlength="maxlength"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @keydown.stop="onInputKeydown"
      />

      <div
        class="absolute inset-y-0 right-4 flex items-center gap-x-3 mt-1 z-10"
      >
        <button
          v-if="(inputValue || '').length > 0"
          type="button"
          class="text-color-3 hover:text-color-1 transition-colors"
          @mousedown.prevent
          @click="onClear"
        >
          <svg-icon name="ic-erase-input-close" class="!w-6 !h-6" />
        </button>

        <button
          v-else
          type="button"
          class="text-color-3 hover:text-brand transition-colors"
          @mousedown.prevent
          @click="onUsersClick"
        >
          <svg-icon name="ic-user" class="!w-6 !h-6" />
        </button>
      </div>
    </div>
    <div v-if="danger" class="text-error text-sm">
      {{ errorMessage }}
    </div>

    <!-- autocomplete only on desktop; z-[100] чтобы поверх модалки -->
    <div
      v-if="isDesktop && showDropdown && suggestions.length > 0"
      class="absolute left-0 right-0 top-full z-[100] mt-1 rounded-3 border border-bg-5 bg-bg-3 p-2 max-h-69.5 overflow-y-auto"
    >
      <button
        v-for="(item, index) in suggestions"
        :key="getItemKey(item)"
        type="button"
        class="w-full text-left px-5 py-3 text-color-1 hover:bg-bg-5 rounded-lg transition-colors text-sm flex flex-col gap-y-0.5"
        :class="{
          'bg-bg-5': index === activeIndex,
        }"
        @mousedown.prevent="onSelectItem(item)"
      >
        <span>{{ getItemLabel(item) }}</span>
        <span
          v-if="itemSublabel !== undefined && itemSublabel !== null"
          class="text-color-3 text-xs"
        >
          {{ getItemSublabel(item) }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

import BaseInput from '@emcd-shared/base-input'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, null],
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  danger: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  minChars: {
    type: Number,
    default: 3,
  },
  debounceMs: {
    type: Number,
    default: 500,
  },
  /** Массив элементов для выпадающего списка. Если передан — используется он, при вводе эмитится @search(query). */
  items: {
    type: Array,
    default: null,
  },
  /** (search: string) => Promise<items[]>. Используется, если не передан items. */
  fetchItems: {
    type: Function,
    default: null,
  },
  /** Поле или функция для ключа элемента (для :key). По умолчанию 'id'. */
  itemKey: {
    type: [String, Function],
    default: 'id',
  },
  /** Поле или функция — первая строка в пункте списка. По умолчанию 'email'. */
  itemLabel: {
    type: [String, Function],
    default: 'email',
  },
  /** Поле или функция — вторая строка под первой (например, comment). */
  itemSublabel: {
    type: [String, Function],
    default: null,
  },
  /** Поле или функция — значение, подставляемое в input при выборе. По умолчанию 'email'. */
  itemValue: {
    type: [String, Function],
    default: 'email',
  },
  /** Максимальная длина вводимого текста */
  maxlength: {
    type: [Number, String],
    default: undefined,
  },
})

const emits = defineEmits(['update:modelValue', 'select', 'search'])

const inputValue = computed({
  get() {
    const v = props.modelValue
    return v !== null && v !== undefined ? String(v) : ''
  },
  set(val) {
    let value = val || null
    if (value && props.maxlength) {
      value = value.slice(0, Number(props.maxlength))
    }
    emits('update:modelValue', value)
  },
})

const wrapperRef = ref(null)
const showDropdown = ref(false)
const internalSuggestions = ref([])
const loading = ref(false)
const isDesktop = ref(false)
const searchEmittedForItems = ref(false)
const activeIndex = ref(-1)
let debounceTimer = null
let blurTimer = null
let clickOutsideHandler = null

function resolveField(item, field) {
  if (typeof field === 'function') return field(item)
  if (typeof field === 'string') return item?.[field]
  return undefined
}

function getItemKey(item) {
  return resolveField(item, props.itemKey) ?? Math.random()
}

function getItemLabel(item) {
  return resolveField(item, props.itemLabel) ?? ''
}

function getItemSublabel(item) {
  if (props.itemSublabel === undefined || props.itemSublabel === null) return ''
  return resolveField(item, props.itemSublabel) ?? ''
}

function getItemValue(item) {
  return resolveField(item, props.itemValue)
}

function checkDesktop() {
  if (typeof window === 'undefined') return
  isDesktop.value = window.innerWidth >= 1024
}

const suggestions = computed(() => {
  if (props.items != null && Array.isArray(props.items)) return props.items
  return internalSuggestions.value
})

async function loadSuggestions(query, { force = false } = {}) {
  const trimmed = (query || '').trim()
  if (!force && (!trimmed || trimmed.length < props.minChars)) {
    internalSuggestions.value = []
    return
  }
  if (props.items != null) {
    searchEmittedForItems.value = true
    emits('search', trimmed)
    return
  }
  if (typeof props.fetchItems !== 'function') return
  loading.value = true
  try {
    const items = await props.fetchItems(trimmed)
    internalSuggestions.value = Array.isArray(items) ? items : []
  } catch {
    internalSuggestions.value = []
  } finally {
    loading.value = false
  }
}

function onInputFocus() {
  const q = (inputValue.value || '').trim()
  if (q.length >= props.minChars && isDesktop.value) {
    loadSuggestions(q)
    showDropdown.value = true
  }
}

function onInputBlur() {
  blurTimer = setTimeout(() => {
    showDropdown.value = false
    searchEmittedForItems.value = false
    activeIndex.value = -1
  }, 200)
}

function onSelectItem(item) {
  showDropdown.value = false
  activeIndex.value = -1
  const value = getItemValue(item)
  inputValue.value = value !== undefined && value !== null ? String(value) : ''
  emits('select', item)
}

function onClear() {
  inputValue.value = ''
  internalSuggestions.value = []
  showDropdown.value = false
  searchEmittedForItems.value = false
  activeIndex.value = -1
  if (props.items != null) {
    emits('search', '')
  }
}

function onUsersClick() {
  if (!isDesktop.value) return

  const q = (inputValue.value || '').trim()
  loadSuggestions(q, { force: true })
  showDropdown.value = true
}

function onInputKeydown(event) {
  if (!isDesktop.value) return

  const key = event.key
  const list = suggestions.value

  if (key === 'ArrowDown' || key === 'ArrowUp') {
    event.preventDefault()
    if (!list.length) {
      loadSuggestions(inputValue.value, { force: true })
      return
    }
    if (activeIndex.value === -1) {
      activeIndex.value = key === 'ArrowDown' ? 0 : list.length - 1
    } else if (key === 'ArrowDown') {
      activeIndex.value = (activeIndex.value + 1) % list.length
    } else if (key === 'ArrowUp') {
      activeIndex.value = (activeIndex.value - 1 + list.length) % list.length
    }

    showDropdown.value = true
    return
  }

  if (key === 'Enter') {
    if (
      showDropdown.value &&
      activeIndex.value >= 0 &&
      list[activeIndex.value]
    ) {
      event.preventDefault()
      onSelectItem(list[activeIndex.value])
    }
    return
  }

  if (key === 'Escape') {
    event.preventDefault()
    showDropdown.value = false
    activeIndex.value = -1
  }
}

watch(
  () => inputValue.value,
  (val) => {
    if (debounceTimer) clearTimeout(debounceTimer)
    const q = (val || '').trim()
    if (!isDesktop.value) return
    if (q.length < props.minChars) {
      internalSuggestions.value = []
      showDropdown.value = false
      searchEmittedForItems.value = false
      if (props.items != null) emits('search', '')
      return
    }
    debounceTimer = setTimeout(() => {
      loadSuggestions(q)
      if (props.items == null) showDropdown.value = true
    }, props.debounceMs)
  }
)

watch(
  () => suggestions.value.length,
  (len) => {
    if (props.items != null && len > 0 && searchEmittedForItems.value) {
      showDropdown.value = true
    }
  }
)

onMounted(() => {
  checkDesktop()
  window.addEventListener('resize', checkDesktop)

  clickOutsideHandler = (event) => {
    const wrapper = wrapperRef.value
    if (!wrapper) return
    if (!wrapper.contains(event.target)) {
      showDropdown.value = false
      searchEmittedForItems.value = false
      activeIndex.value = -1
    }
  }
  document.addEventListener('mousedown', clickOutsideHandler)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkDesktop)
  if (debounceTimer) clearTimeout(debounceTimer)
  if (blurTimer) clearTimeout(blurTimer)
  if (clickOutsideHandler) {
    document.removeEventListener('mousedown', clickOutsideHandler)
    clickOutsideHandler = null
  }
})
</script>
